import { useMemo, useState } from "react";
import { Bell, ChevronDown, ChevronUp } from "lucide-react";
import { UpgradePrompt } from "../../components/billing/UpgradePrompt";
import { Switch } from "../../components/ui/switch";
import { useGroupTier } from "../../hooks/useGroupTier";
import {
  UPCOMING_REMINDER_OFFSETS_MINUTES,
  VOTE_REMINDER_OFFSETS_MINUTES,
  normalizeReminderSettings,
} from "./reminder-settings";
import { ReminderTimingControl } from "./ReminderTimingControl";

function withDiscordAvailability(settings, discordLinked) {
  if (discordLinked || settings.channels.discord === false) return settings;
  return {
    ...settings,
    channels: { ...settings.channels, discord: false },
  };
}

function LaneCheckbox({ id, label, description, checked, disabled, onChange }) {
  return (
    <label
      htmlFor={id}
      className={`flex items-start gap-3 rounded-xl border border-slate-200/70 px-3 py-2 dark:border-slate-700 ${
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
      }`}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-primary focus:ring-brand-accent"
      />
      <span>
        <span className="block text-sm font-medium text-slate-700 dark:text-slate-200">{label}</span>
        <span className="block text-xs text-slate-500 dark:text-slate-400">{description}</span>
      </span>
    </label>
  );
}

export function AutomatedReminderSettings({ group, isOwner, onSave }) {
  const discordLinked = Boolean(group?.discord?.channelId);
  const normalized = useMemo(
    () => normalizeReminderSettings(group?.reminders, { discordLinked }),
    [group?.reminders, discordLinked]
  );
  const initialSettings = useMemo(
    () => withDiscordAvailability(normalized.settings, discordLinked),
    [normalized.settings, discordLinked]
  );
  const [draft, setDraft] = useState(initialSettings);
  const [expanded, setExpanded] = useState(initialSettings.enabled || !normalized.valid);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [savedSettings, setSavedSettings] = useState(initialSettings);
  const tier = useGroupTier(group?.creatorId || null);
  const automationEntitled = tier.loading || tier.canUseFeature("AUTOMATED_REMINDERS");
  const rolePingEntitled = !tier.loading && tier.canUseFeature("DISCORD_ROLE_PINGS");
  const canEdit = isOwner && automationEntitled && !saving;
  const dirty = JSON.stringify(draft) !== JSON.stringify(savedSettings);

  const updateKind = (key, value) => {
    setMessage("");
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const updateChannel = (key, checked) => {
    const nextChannels = { ...draft.channels, [key]: checked };
    if (draft.enabled && !Object.values(nextChannels).some(Boolean)) {
      setError("Choose at least one delivery option while automatic reminders are on.");
      return;
    }
    setError("");
    setMessage("");
    setDraft((current) => ({ ...current, channels: nextChannels }));
  };

  const updateDiscord = (key, value) => {
    setError("");
    setMessage("");
    setDraft((current) => ({
      ...current,
      discord: { ...current.discord, [key]: value },
    }));
  };

  const toggleMaster = (enabled) => {
    if (enabled && !Object.values(draft.channels).some(Boolean)) {
      setError("Choose at least one delivery option before turning on automatic reminders.");
      setExpanded(true);
      return;
    }
    setError("");
    setMessage("");
    setExpanded(enabled || expanded);
    setDraft((current) => ({ ...current, enabled }));
  };

  const handleSave = async () => {
    const validation = normalizeReminderSettings(draft, { discordLinked });
    if (!validation.valid) {
      setError("Review the reminder timings and delivery options before saving.");
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await onSave(validation.settings);
      setDraft(validation.settings);
      setSavedSettings(validation.settings);
      setMessage("Saved");
    } catch (saveError) {
      setError(saveError?.message || "Could not save automatic reminders. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const activeKinds = [draft.sessionVotes, draft.basicPollVotes, draft.upcomingSessions]
    .filter((kind) => kind.enabled).length;
  const activeLanes = Object.entries(draft.channels)
    .filter(([, enabled]) => enabled)
    .map(([key]) => ({ inApp: "In-app", email: "Email", discord: "Discord" })[key]);

  return (
    <section
      data-testid="automated-reminder-settings"
      className="rounded-2xl border border-violet-200/70 bg-violet-50/30 p-4 dark:border-violet-800/60 dark:bg-violet-950/10"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 gap-3">
          <span className="rounded-xl bg-violet-100 p-2 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200">
            <Bell className="h-4 w-4" aria-hidden="true" />
          </span>
          <div>
            <label htmlFor="automated-reminders-enabled" className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              Automatic reminders
            </label>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {draft.enabled
                ? `${activeKinds} reminder ${activeKinds === 1 ? "type" : "types"} · ${activeLanes.join(", ")}`
                : "Off — no reminders will be sent automatically"}
            </p>
          </div>
        </div>
        <Switch
          id="automated-reminders-enabled"
          checked={draft.enabled}
          onCheckedChange={toggleMaster}
          disabled={!canEdit}
          aria-label="Automatic reminders enabled"
        />
      </div>

      {!isOwner && (
        <p className="mt-3 rounded-xl bg-white/70 px-3 py-2 text-xs text-slate-600 dark:bg-slate-900/50 dark:text-slate-300">
          Only the group owner can change automatic reminders.
        </p>
      )}

      {isOwner && !tier.loading && !automationEntitled && (
        <div className="mt-3">
          <UpgradePrompt
            feature="AUTOMATED_REMINDERS"
            tier="lite"
            message={
              group?.reminders
                ? "Your saved reminder schedule is paused. Upgrade the group owner to Lite to resume it."
                : "Upgrade the group owner to Lite to send reminders automatically for the whole table."
            }
          />
        </div>
      )}

      {!normalized.valid && (
        <p role="alert" className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
          Saved reminder settings could not be read safely. Review the defaults below and save again.
        </p>
      )}

      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
        aria-controls="automated-reminder-details"
        className="mt-3 flex min-h-[40px] w-full items-center justify-between rounded-xl px-2 text-xs font-semibold text-violet-700 hover:bg-violet-100/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent dark:text-violet-200 dark:hover:bg-violet-900/20"
      >
        {expanded ? "Hide reminder details" : "Customize reminder schedule"}
        {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      {expanded && (
        <div id="automated-reminder-details" className="mt-3 space-y-4">
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
              What to remind about
            </h4>
            <div className="mt-2 space-y-3">
              <ReminderTimingControl
                id="session-vote-reminders"
                title="Session poll votes"
                description="Remind current non-voters before this session poll's explicit voting deadline. Polls without a deadline are skipped."
                value={draft.sessionVotes}
                allowedOffsets={VOTE_REMINDER_OFFSETS_MINUTES}
                disabled={!canEdit}
                onChange={(value) => updateKind("sessionVotes", value)}
                onConstraint={setError}
              />
              <ReminderTimingControl
                id="general-poll-reminders"
                title="General poll votes"
                description="Remind non-voters before deadlines on group polls and required add-on polls. Polls without a deadline are skipped."
                value={draft.basicPollVotes}
                allowedOffsets={VOTE_REMINDER_OFFSETS_MINUTES}
                disabled={!canEdit}
                onChange={(value) => updateKind("basicPollVotes", value)}
                onConstraint={setError}
              />
              <ReminderTimingControl
                id="upcoming-session-reminders"
                title="Upcoming sessions"
                description="Remind the group before the start time of a finalized session."
                value={draft.upcomingSessions}
                allowedOffsets={UPCOMING_REMINDER_OFFSETS_MINUTES}
                disabled={!canEdit}
                onChange={(value) => updateKind("upcomingSessions", value)}
                onConstraint={setError}
              />
            </div>
          </div>

          <fieldset>
            <legend className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
              Where to send
            </legend>
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              <LaneCheckbox
                id="reminder-lane-in-app"
                label="In-app"
                description="Uses each person's preferences."
                checked={draft.channels.inApp}
                disabled={!canEdit}
                onChange={(checked) => updateChannel("inApp", checked)}
              />
              <LaneCheckbox
                id="reminder-lane-email"
                label="Email"
                description="Each person can opt out."
                checked={draft.channels.email}
                disabled={!canEdit}
                onChange={(checked) => updateChannel("email", checked)}
              />
              <LaneCheckbox
                id="reminder-lane-discord"
                label="Discord"
                description={discordLinked ? "Posts in the linked channel." : "Link a channel first."}
                checked={draft.channels.discord}
                disabled={!canEdit || !discordLinked}
                onChange={(checked) => updateChannel("discord", checked)}
              />
            </div>
            {!discordLinked && (
              <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                Generate a Discord link code above to enable channel reminders.
              </p>
            )}
          </fieldset>

          {discordLinked && draft.channels.discord && (
            <fieldset className="rounded-2xl border border-slate-200/70 p-3 dark:border-slate-700">
              <legend className="px-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                Discord mentions
              </legend>
              <div className="mt-1 space-y-3 text-xs text-slate-600 dark:text-slate-300">
                <div>
                  <p className="font-semibold text-slate-700 dark:text-slate-200">Vote reminders</p>
                  <label className="mt-2 flex min-h-[36px] items-center gap-2">
                    <input
                      type="radio"
                      name={`vote-mention-${group?.id}`}
                      value="nonVoters"
                      checked={draft.discord.voteMentionMode === "nonVoters"}
                      disabled={!canEdit}
                      onChange={() => updateDiscord("voteMentionMode", "nonVoters")}
                    />
                    Mention linked non-voters when vote visibility permits
                  </label>
                  <label className="flex min-h-[36px] items-center gap-2">
                    <input
                      type="radio"
                      name={`vote-mention-${group?.id}`}
                      value="none"
                      checked={draft.discord.voteMentionMode === "none"}
                      disabled={!canEdit}
                      onChange={() => updateDiscord("voteMentionMode", "none")}
                    />
                    Post without mentions
                  </label>
                </div>
                {draft.upcomingSessions.enabled && (
                  <div className="border-t border-slate-200/70 pt-3 dark:border-slate-700">
                    <p className="font-semibold text-slate-700 dark:text-slate-200">Upcoming sessions</p>
                    <label className="mt-2 flex min-h-[36px] items-center gap-2">
                      <input
                        type="radio"
                        name={`upcoming-mention-${group?.id}`}
                        value="none"
                        checked={draft.discord.upcomingMentionMode === "none"}
                        disabled={!canEdit}
                        onChange={() => updateDiscord("upcomingMentionMode", "none")}
                      />
                      Do not mention anyone
                    </label>
                    <label className="flex min-h-[36px] items-center gap-2">
                      <input
                        type="radio"
                        name={`upcoming-mention-${group?.id}`}
                        value="notifyRole"
                        checked={draft.discord.upcomingMentionMode === "notifyRole"}
                        disabled={!canEdit || !rolePingEntitled}
                        onChange={() => updateDiscord("upcomingMentionMode", "notifyRole")}
                      />
                      Mention the channel notification role
                    </label>
                    {!rolePingEntitled && (
                      <p className="text-[11px] text-amber-700 dark:text-amber-300">
                        Notification-role mentions require Pro. No-mention reminders still work on Lite.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </fieldset>
          )}

          <div className="rounded-xl bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-600 dark:bg-slate-800/70 dark:text-slate-300">
            <p>Vote reminders are measured before an explicit poll deadline. People who already voted are skipped automatically.</p>
            <p className="mt-1">These become the defaults for new polls in this questing group. A poll can override them without changing the group defaults.</p>
          </div>

          {error && (
            <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-200">
              {error}
            </p>
          )}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p aria-live="polite" className="min-h-5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
              {message}
            </p>
            {isOwner && automationEntitled && (
              <button
                type="button"
                onClick={handleSave}
                disabled={!dirty || saving}
                className="min-h-[40px] rounded-full bg-brand-primary px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-brand-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save reminders"}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
